// @ts-nocheck

// App
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';

// Lodash
import isEmpty from 'lodash/isEmpty';

// Actions
import { FilterActions_fetchPriceHistogramFilterData } from 'app/shared/flux/actions/FilterActions';

// Components
import HistogramSlider from 'app/shared/modules/MobileSRP/Header/FilterModules/PriceFilter/HistogramSlider';

// Hooks
import { useFilterChange } from 'app/shared/modules/filter/FilterBar/hooks';

// Misc / utils
import VALUE_CONSTANTS from 'app/shared/constants/ValueConstants';
import { PRICE_SLIDER_MAX } from 'app/shared/constants/FilterConstants';
import {
  GeneralFilterHeader,
  GeneralFilterLabel,
  GeneralFilterResetButton,
  GeneralFilterSection,
} from 'app/shared/modules/filter/FilterBar/styles';
import routeUtils from 'app/shared/utils/routeUtils';
import { DEFAULT } from 'app/shared/models/Filter';
import * as S from './styles';
import {
  emitTrackHighPriceFilterEvent,
  emitTrackLowPriceFilterEvent,
} from 'app/shared/models/Clickstream/FilterClickstreamEvents';

const PriceFilter = ({ showResetButton = false }) => {
  // Redux
  const dispatch = useDispatch();
  const priceState = useSelector((state) => state.filter.price);
  const histogramData = useSelector((state) => state.area.priceHistogram);
  const ssrEntryPathname = useSelector((state) => state.location.ssrEntry.pathname);

  // React state
  const didAppHaveHdpSsrEntry =
    routeUtils.isPadUrl(ssrEntryPathname) || routeUtils.isPadOrBuildingUrl(ssrEntryPathname);
  const shouldFetchNewData = didAppHaveHdpSsrEntry && histogramData?.data?.length < 100;
  const [filterState, setFilterState] = useState(priceState);
  const [isAsyncDataFetching, setIsAsyncDataFetching] = useState(false);
  const [asyncData, setAsyncData] = useState(shouldFetchNewData ? null : histogramData.data);
  const [shouldDisplayHistogram, setShouldDisplayHistogram] = useState(false);

  // Hooks
  const { reduxOnlyChange, resetFilter } = useFilterChange();

  const handleResetClick = useYieldCallback(() => {
    resetFilter({ price: DEFAULT.price });
    setFilterState({ min: null, max: null });
  });

  const handlePriceSelection = (rawVals, isPriceSetViaInput = false) => {
    const [min, max] = rawVals;
    const MULTIPLIER = VALUE_CONSTANTS.PRICE_FILTER_INCREMENT;
    const formattedVals = {
      min: min === 0 ? null : min * MULTIPLIER,
      max: max === PRICE_SLIDER_MAX ? null : max * MULTIPLIER,
    };

    const newFilterState = { min: formattedVals.min, max: formattedVals.max };
    setFilterState(newFilterState);
    reduxOnlyChange({ price: newFilterState });

    if (formattedVals.min !== filterState.min) {
      emitTrackLowPriceFilterEvent({ triggerLocation: 'search_results', triggerObject: 'filter_panel' });
    } else if (formattedVals.max !== filterState.max) {
      emitTrackHighPriceFilterEvent({ triggerLocation: 'search_results', triggerObject: 'filter_panel' });
    }
  };

  const fetchHistogramData = () => {
    dispatch(FilterActions_fetchPriceHistogramFilterData())
      .then(() => setIsAsyncDataFetching(false))
      .then(() => setAsyncData(histogramData.data));
  };

  useEffect(() => {
    if (isEmpty(asyncData) || shouldFetchNewData) {
      setIsAsyncDataFetching(true);
      fetchHistogramData();
    }
  }, []);

  useEffect(() => {
    if (histogramData?.data?.length > 51) {
      setShouldDisplayHistogram(true);
    }
  }, [histogramData]);

  return (
    <GeneralFilterSection>
      <GeneralFilterHeader>
        <GeneralFilterLabel id="price-label">Price range</GeneralFilterLabel>
        {showResetButton && (
          <GeneralFilterResetButton onClick={handleResetClick} aria-label={'Reset all active price filters'}>
            Reset
          </GeneralFilterResetButton>
        )}
      </GeneralFilterHeader>

      {shouldDisplayHistogram ? (
        <HistogramSlider
          role="group"
          ariaLabelledBy="price-label"
          histogramData={histogramData}
          minPrice={filterState.min ? filterState.min / VALUE_CONSTANTS.PRICE_FILTER_INCREMENT : null}
          maxPrice={filterState.max ? filterState.max / VALUE_CONSTANTS.PRICE_FILTER_INCREMENT : null}
          handlePriceChange={handlePriceSelection}
        />
      ) : (
        <>{isAsyncDataFetching ? <S.Skeleton /> : <HistogramSlider disabled />}</>
      )}
    </GeneralFilterSection>
  );
};

export default PriceFilter;
